import { apiClient, toQueryParams } from './client';
import type { BusinessUnit, Region } from '@/types';

/** Raw shape returned by the backend (BusinessUnitDto). */
interface BusinessUnitDto {
  businessUnitId: number;
  brandId: number;
  name: string | null;
  description: string | null;
  isActive: boolean;
  createdDate: string;
  regionIds: number[] | null;
}

interface RegionDto {
  regionId: number;
  name: string | null;
  code: string | null;
  isActive: boolean;
}

interface RegionDtoPagedResult {
  items: RegionDto[] | null;
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

function toBusinessUnit(dto: BusinessUnitDto): BusinessUnit {
  return {
    id: dto.businessUnitId,
    brandId: dto.brandId,
    name: dto.name ?? '',
    description: dto.description ?? undefined,
    isActive: dto.isActive,
    createdAt: dto.createdDate,
    regionIds: dto.regionIds ?? [],
  };
}

function toRegion(dto: RegionDto): Region {
  return {
    id: dto.regionId,
    name: dto.name ?? '',
    code: dto.code ?? '',
    isActive: dto.isActive,
  };
}

export interface BusinessUnitCreateInput {
  brandId: number;
  name: string;
  description?: string;
  regionIds?: number[];
}

export interface BusinessUnitUpdateInput {
  name: string;
  description?: string;
  isActive: boolean;
  regionIds?: number[];
}

export interface RegionListParams {
  search?: string;
  includeInactive?: boolean;
  page?: number;
  pageSize?: number;
}

export const businessUnitsApi = {
  /** The backend only exposes business units scoped to a brand. */
  listByBrand: async (brandId: number): Promise<BusinessUnit[]> => {
    const { data } = await apiClient.get<BusinessUnitDto[]>(
      `/brands/${brandId}/business-units`,
    );
    return data.map(toBusinessUnit);
  },
  getById: async (id: number): Promise<BusinessUnit> => {
    const { data } = await apiClient.get<BusinessUnitDto>(`/business-units/${id}`);
    return toBusinessUnit(data);
  },
  create: async (input: BusinessUnitCreateInput): Promise<BusinessUnit> => {
    const { data } = await apiClient.post<BusinessUnitDto>('/business-units', input);
    return toBusinessUnit(data);
  },
  update: async (id: number, input: BusinessUnitUpdateInput): Promise<BusinessUnit> => {
    const { data } = await apiClient.put<BusinessUnitDto>(`/business-units/${id}`, input);
    return toBusinessUnit(data);
  },
  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/business-units/${id}`);
  },
  /** Top-level region catalog (GET /regions), paged — defaults to a large page for dropdowns. */
  listRegions: async (params: RegionListParams = {}): Promise<Region[]> => {
    const { data } = await apiClient.get<RegionDtoPagedResult>('/regions', {
      params: toQueryParams({ page: 1, pageSize: 200, ...params }),
    });
    return (data.items ?? []).map(toRegion);
  },
};
